import { useState, type FormEvent } from "react";
import { api, errorMessage } from "../../services/api.js";
import { useAsync } from "../../lib/useAsync.js";
import { DEMO_LOCK_NOTE, useDemo } from "./AdminLayout.js";
import { Alert, ConfirmDialog, ErrorAlert, PageHeader, Spinner, Stat } from "../../components/ui/index.js";

/** Activar o desactivar el modo demo (requiere la contraseña root del servidor). */
function DemoModeCard() {
  const status = useAsync(() => api.demoStatus(), []);
  const [root, setRoot] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const s = status.data;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!s) return;
    setBusy(true);
    setMsg(null);
    setErr(null);
    try {
      const next = await api.setDemoMode(!s.enabled, root);
      status.setData(next);
      setRoot("");
      setMsg(next.enabled ? "Modo demo activado." : "Modo demo desactivado.");
    } catch (e2) {
      setErr(errorMessage(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card space-y-3" aria-labelledby="demo-title" data-testid="demo-card" data-tour="settings-demo">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="demo-title" className="text-lg font-semibold">
          Modo demo
        </h2>
        {s && <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${s.enabled ? "bg-amber-100 text-amber-900" : "bg-slate-100 text-slate-700"}`}>{s.enabled ? "Activo" : "Inactivo"}</span>}
      </div>
      <ErrorAlert error={status.error} />
      {s && (
        <>
          <p className="text-sm text-slate-600">
            Limita la instalación a {s.limits.studies} estudio, {s.limits.participations} participaciones y {s.limits.mediaItems} imágenes o videos en total, y muestra un
            aviso en el panel, el inicio de sesión y las pantallas de participación. Lo que ya existe no se borra; solo se bloquea crear más.
          </p>
          <p className="font-util text-xs text-slate-500">
            Uso actual: estudios {s.usage.studies} · participaciones {s.usage.participations} · imágenes o videos {s.usage.mediaItems}
          </p>
          {s.configurable ? (
            <form onSubmit={submit} className="flex flex-wrap items-end gap-2">
              <div className="min-w-56 flex-1">
                <label htmlFor="demo-root" className="label">
                  Contraseña root
                </label>
                <input id="demo-root" type="password" autoComplete="off" className="input" value={root} onChange={(e) => setRoot(e.target.value)} required />
              </div>
              <button type="submit" className={s.enabled ? "btn-secondary" : "btn-primary"} disabled={busy || !root} data-testid="demo-toggle">
                {busy ? "Guardando…" : s.enabled ? "Desactivar modo demo" : "Activar modo demo"}
              </button>
            </form>
          ) : (
            <Alert tone="info">El servidor no tiene contraseña root (variable DEMO_ROOT_PASSWORD): el modo demo no se puede cambiar.</Alert>
          )}
          {msg && <p className="text-sm text-emerald-700">{msg}</p>}
          <ErrorAlert error={err} />
        </>
      )}
    </section>
  );
}

export function SettingsPage() {
  const info = useAsync(() => api.systemInfo(), []);
  const locked = useDemo()?.enabled === true;
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeMsg, setPurgeMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const i = info.data;


  return (
    <div className="space-y-4">
      <PageHeader title="Ajustes" description="Datos del sistema, política de retención, exportación y eliminación de datos de participantes." />
      <ErrorAlert error={error ?? info.error} />
      {info.loading && !i ? (
        <Spinner />
      ) : (
        i && (
          <section className="card space-y-3" aria-labelledby="sys-title" data-tour="settings-system">
            <h2 id="sys-title" className="text-lg font-semibold">
              Sistema
            </h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Stat label="Versión" value={i.version} />
              <Stat label="Node.js" value={i.nodeVersion} />
              <Stat label="Algoritmo de análisis" value={<span className="text-sm">{i.algorithmVersion}</span>} />
              <Stat label="Captura sintética" value={i.syntheticCaptureEnabled ? "Habilitada (desarrollo)" : "Deshabilitada"} />
              <Stat label="Estudios" value={i.counts.studies} />
              <Stat label="Sesiones (total)" value={i.counts.sessions} />
              <Stat label="Sesiones sintéticas" value={i.counts.syntheticSessions} />
              <Stat label="Muestras de mirada" value={i.counts.samples.toLocaleString("es-MX")} />
            </div>
            <p className="text-sm text-slate-600">
              Límites de carga: {i.uploadLimits.maxImageSizeMb} MB por archivo, {i.uploadLimits.maxImagePixels.toLocaleString("es-MX")} píxeles máximo; formatos{" "}
              {i.uploadLimits.allowedMimeTypes.join(", ")}. Videos: hasta {i.uploadLimits.maxVideoSizeMb} MB por archivo;{" "}
              {i.uploadLimits.videoProcessingAvailable ? "FFmpeg disponible para convertirlos." : <strong className="text-amber-800">FFmpeg no está instalado en el servidor: no se pueden subir videos.</strong>}
            </p>
          </section>
        )
      )}

      <div className="grid items-start gap-4 xl:grid-cols-2">
        <div className="space-y-4">
      <section className="card space-y-3" aria-labelledby="ret-title" data-tour="settings-retention">
        <h2 id="ret-title" className="text-lg font-semibold">
          Retención de datos
        </h2>
        <p className="text-sm text-slate-700">
          Cada estudio define cuántos días se conservan sus sesiones («Retención (días)» en Configuración; predeterminado {i?.defaultRetentionDays ?? 180} días). La purga elimina en cascada
          sesiones vencidas con sus calibraciones, ensayos y muestras; los resultados se recalculan. También puede ejecutarse por línea de comandos con <code>pnpm db:purge</code>.
        </p>
        <p className="text-sm">
          Sesiones vencidas actualmente: <strong data-testid="expired-count">{i?.expiredSessions ?? "—"}</strong>
        </p>
        {purgeMsg && <Alert tone="success">{purgeMsg}</Alert>}
        <button type="button" className="btn-danger" onClick={() => setPurgeOpen(true)} disabled={locked || !i || i.expiredSessions === 0} title={locked ? DEMO_LOCK_NOTE : undefined}>
          Purgar sesiones vencidas
        </button>
      </section>

        </div>
        <div className="space-y-4">
      <section className="card space-y-2" aria-labelledby="del-title">
        <h2 id="del-title" className="text-lg font-semibold">
          Eliminación de datos de participantes
        </h2>
        <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
          <li>
            Para borrar una sesión concreta ve a <em>Estudio → Participantes → Borrar</em>. Se eliminan sesión, calibraciones, ensayos y muestras.
          </li>
          <li>
            <strong>Solicitudes de eliminación:</strong> al terminar, cada persona ve su <strong>código de participante</strong> y el contacto del estudio
            (Configuración → Contacto). Si te pide borrar sus datos, búscalo en <em>Participantes</em> con ese código y bórralo.
          </li>
          <li>Excluir una sesión del análisis no borra datos; solo la retira de mapas y métricas, con motivo auditado.</li>
          <li>No se guardan nombres, correos, video, fotografías del rostro ni audio.</li>
        </ul>
      </section>

      <section className="card" aria-labelledby="exp-title">
        <h2 id="exp-title" className="text-lg font-semibold">
          Exportación
        </h2>
        <p className="mt-1 text-sm text-slate-700">
          Las exportaciones CSV/JSON se generan por estudio (pestaña «Exportar») y siempre para una sola fuente de datos (real o sintética). Los CSV neutralizan celdas que podrían
          interpretarse como fórmulas.
        </p>
      </section>

      <DemoModeCard />

        </div>
      </div>

      <ConfirmDialog
        open={purgeOpen}
        title="Purgar sesiones vencidas"
        danger
        requireText="PURGAR"
        message={<p>Se eliminarán definitivamente {i?.expiredSessions ?? 0} sesiones cuya antigüedad supera la retención de su estudio.</p>}
        confirmLabel="Purgar"
        onCancel={() => setPurgeOpen(false)}
        onConfirm={async () => {
          setPurgeOpen(false);
          setError(null);
          try {
            const r = await api.purgeExpired();
            setPurgeMsg(`Se eliminaron ${r.deletedSessions} sesiones vencidas.`);
            await info.reload();
          } catch (e) {
            setError(errorMessage(e));
          }
        }}
      />
    </div>
  );
}
